/* eslint-disable */
import {makeAutoObservable, runInAction} from "mobx";
import Select from "formAdapters/Select";
import request from "utils/request";
import {createRangeValidator, required} from "utils/validate";
import {FORM_ERROR} from "final-form";
import CustomInputNumber from "../../../../../../../../../../../../formAdapters/InputNumber";
import {t} from "i18next";
import moment from "moment";

class CommonTrendParameterConstraintsStore {
    trendCommonAnalysisSettings = [];
    wellsTrendIndividualAnalysisSettings = [];
    parameters = [];

    constructor() {
        makeAutoObservable(this);
    }

    get addCommonTrendParameterConstraintsFields() {
        return {
            parameter: {
                label: "parameter",
                name: "parameterId",
                component: Select,
                allowClear: true,
                options: this.parameters,
                validate: required
            },
            approxConstraints: [{
                label: t("minimumLimit"),
                name: "approxConstraints1",
                defaultValue: -0.001,
                component: CustomInputNumber,
                validate: createRangeValidator(-999, 0, true),
            },
                {
                    label: t("maximumLimit"),
                    name: "approxConstraints2",
                    defaultValue: 0.001,
                    component: CustomInputNumber,
                    validate: createRangeValidator(0, 999, true)
                }],
            deltaValueConstraint: {
                label: t("deltaOfLoadingAndDeltaCurrent"),
                name: "deltaValueConstraint",
                defaultValue: 9,
                component: CustomInputNumber,
                validate: createRangeValidator(0, 999, true)
            },
            sawConstraints: [{
                label: t("timeIntervalInMinutes"),
                name: "sawConstraints1",
                defaultValue: 60,
                component: CustomInputNumber,
                validate: createRangeValidator(0, 999, true)
            },
                {
                    label: t("numberOfChangesPerInterval"),
                    name: "sawConstraints2",
                    defaultValue: 10,
                    component: CustomInputNumber,
                    validate: createRangeValidator(0, 999, true)
                },
                {
                    label: t("deltaOfParameterPerInterval"),
                    name: "sawConstraints3",
                    defaultValue: 5,
                    component: CustomInputNumber,
                    validate: createRangeValidator(0, 999, true)
                }],
        }
    }

    async fetchParameters() {
        try {
            const {data} = await request.get("settings/trends/get/paramsList");
            runInAction(() => {
                this.parameters = data.map(param => ({id: param.id, name: param.name}));
            });
        } catch (error) {
            console.error("Failed to fetch parameters:", error);
            runInAction(() => {
                this.parameters = [];
            });
        }
    }

    async addCommonTrendParameterConstraints(groupId, values) {
        try {
            const {data} = await request.post("/trend-analysis-settings/common-trend-parameter-constraints", values);
            const {commonTrendParameterConstraintsEntityId, individualTrendParameterConstraintsIdAndWellId} = data
            runInAction(() => {
                const groupSettings = this.trendCommonAnalysisSettings.find(
                    setting => setting.trendGroupName === groupId
                );

                if (groupSettings) {
                    groupSettings.trendConstraints.push({
                        ...values,
                        approxConstraintsFirst: values.approxConstraints[0],
                        approxConstraintsLast: values.approxConstraints[1],
                        sawConstraint1: values.sawConstraints[0],
                        sawConstraint2: values.sawConstraints[1],
                        sawConstraint3: values.sawConstraints[2],
                        id: commonTrendParameterConstraintsEntityId
                    });
                }

                individualTrendParameterConstraintsIdAndWellId.forEach((individualTrendParameterConstraintIdAndWellId) => {
                    this.wellsTrendIndividualAnalysisSettings
                        .find(trendIndividualAnalysisSettings => individualTrendParameterConstraintIdAndWellId.wellId === trendIndividualAnalysisSettings.wellId)?.trendIndividualSettings
                        .find(trendCommonAnalysisSetting => trendCommonAnalysisSetting.trendGroupName === groupId).trendConstraints
                        .push({
                            ...values,
                            approxConstraintsFirst: values.approxConstraints[0],
                            approxConstraintsLast: values.approxConstraints[1],
                            sawConstraint1: values.sawConstraints[0],
                            sawConstraint2: values.sawConstraints[1],
                            sawConstraint3: values.sawConstraints[2],
                            id: individualTrendParameterConstraintIdAndWellId.id
                        })
                })

            });

            return true;
        } catch (error) {
            console.error(error);
            let message = ""
            if (error?.request?.status === 409) message = t("suchParameterAlreadyExists")
            return {
                [FORM_ERROR]: message
            }
        }
    }

    async deleteCommonTrendParameterConstraints(id, groupId, parameterId) {
        try {
            await request.delete(`/trend-analysis-settings/common-trend-parameter-constraints/${id}`);
            runInAction(() => {
                const groupSettings = this.trendCommonAnalysisSettings.find(
                    setting => setting.trendGroupName === groupId
                );
                if (groupSettings) {
                    groupSettings.trendConstraints = groupSettings.trendConstraints.filter(
                        constraint => constraint.id !== id
                    );
                }

                this.wellsTrendIndividualAnalysisSettings.forEach(wellSettings => {
                    const individualGroupSettings = wellSettings.trendIndividualSettings?.find(
                        setting => setting.trendGroupName === groupId
                    );
                    if (individualGroupSettings) {
                        individualGroupSettings.trendConstraints = individualGroupSettings.trendConstraints.filter(
                            constraint => constraint.parameterId !== parameterId
                        );
                    }
                });
            });
        } catch (error) {
            console.error(error);
            return {
                [FORM_ERROR]: null
            }
        }
    }

    setWellsTrendIndividualAnalysisSettings(wellsTrendIndividualAnalysisSetting) {
        this.wellsTrendIndividualAnalysisSettings = wellsTrendIndividualAnalysisSetting;
    }

    getWellsTrendIndividualAnalysisSettings() {
        return this.wellsTrendIndividualAnalysisSettings;
    }

    setTrendCommonAnalysisSettings(trendCommonAnalysisSetting) {
        this.trendCommonAnalysisSettings = trendCommonAnalysisSetting;
    }

    getTrendCommonAnalysisSettings() {
        return this.trendCommonAnalysisSettings
    }

    async updateTrendIndividualAnalysisSettings(trendGroupName, trendConstraints, wellId, trendSettingsIndividual) {
        let values = {}
        values.trendGroupName = trendGroupName
        values.wellId = wellId
        values.status = Boolean(trendSettingsIndividual.status)
        values.state = Boolean(trendSettingsIndividual.state)
        values.delayResponse = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.delayResponseDays,
            hour: trendSettingsIndividual.delayResponseHours,
            minute: 0,
            second: 0,
        }
        values.minDataFrequency = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.minDataFrequencyDays,
            hour: trendSettingsIndividual.minDataFrequencyHours,
            minute: 0,
            second: 0,
        }
        values.analysisPeriodMin = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.analysisPeriodMinDays,
            hour: trendSettingsIndividual.analysisPeriodMinHours,
            minute: 0,
            second: 0,
        }
        values.analysisPeriodMax = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.analysisPeriodMaxDays,
            hour: trendSettingsIndividual.analysisPeriodMaxHours,
            minute: 0,
            second: 0,
        }
        values.timeDiscr = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.timeDiscrDays,
            hour: trendSettingsIndividual.timeDiscrHours,
            minute: 0,
            second: 0,
        }

        if (trendSettingsIndividual.analysisTime) {
            const utcTime = moment(trendSettingsIndividual.analysisTime).utc();
            values.analysisTime = utcTime.format("HH:mm:ss");
        } else {
            values.analysisTime = null;
        }

        values.unapprovalReason = trendSettingsIndividual.unapprovalReason
        values.mop = {
            year: 0,
            month: 0,
            day: trendSettingsIndividual.mopDays ? trendSettingsIndividual.mopDays : 0,
            hour: trendSettingsIndividual.mopHours ? trendSettingsIndividual.mopHours : 0,
            minute: 0,
            second: 0,
        }

        values.trendConstraints = []

        trendConstraints.forEach(constraint => {
            if (trendSettingsIndividual.hasOwnProperty(constraint.parameterId)) {
                let updatedConstraint = trendSettingsIndividual[`${constraint.parameterId}`]
                updatedConstraint.settingsId = constraint.settingsId
                updatedConstraint.parameterId = constraint.parameterId
                values.trendConstraints.push(updatedConstraint)
            }
        })
        values.lastMopDate = trendSettingsIndividual.lastMopDate._d

        await request.post(`/settings/trend/individual/update/${wellId}`, values);
    }

    async updateTrendCommonAnalysisSettings(trendGroupName, trendConstraints, trendSettingsCommon) {
        let values = {};
        values.trendGroupName = trendGroupName;
        values.status = Boolean(trendSettingsCommon.status);
        values.delayResponse = {
            year: 0,
            month: 0,
            day: 0,
            hour: 0,
            minute: trendSettingsCommon.delayResponseMinutes,
            second: 0,
        };
        values.minDataFrequency = {
            year: 0,
            month: 0,
            day: 0,
            hour: 0,
            minute: trendSettingsCommon.minDataFrequencyMinutes,
            second: 0,
        };
        values.analysisPeriodMin = {
            year: 0,
            month: 0,
            day: trendSettingsCommon.analysisPeriodMinDays,
            hour: trendSettingsCommon.analysisPeriodMinHours,
            minute: 0,
            second: 0,
        };
        values.analysisPeriodMax = {
            year: 0,
            month: 0,
            day: trendSettingsCommon.analysisPeriodMaxDays,
            hour: trendSettingsCommon.analysisPeriodMaxHours,
            minute: 0,
            second: 0,
        };
        values.timeDiscr = {
            year: 0,
            month: 0,
            day: trendSettingsCommon.timeDiscrDays,
            hour: trendSettingsCommon.timeDiscrHours,
            minute: 0,
            second: 0,
        };

        if (trendSettingsCommon.analysisTime) {
            const utcTime = moment(trendSettingsCommon.analysisTime).utc();
            values.analysisTime = utcTime.format("HH:mm:ss");
        } else {
            values.analysisTime = null;
        }

        values.trendConstraints = [];

        trendConstraints.forEach((constraint) => {
            if (trendSettingsCommon.hasOwnProperty(constraint.parameterId)) {
                let updatedConstraint = trendSettingsCommon[`${constraint.parameterId}`];
                updatedConstraint.settingsId = constraint.settingsId;
                updatedConstraint.parameterId = constraint.parameterId;
                values.trendConstraints.push(updatedConstraint);
            }
        });

        await request.post("/settings/trend/common/update", values);
    }
}

export default CommonTrendParameterConstraintsStore;
